/**
 * Copy de los estados bloqueados (Figma 3352-22215), por key del registry de la
 * api (api/src/plans/registry.js). `benefits` define la variante del modal;
 * `icon` es el Material Symbol del diseno.
 *
 * `availability` es el texto de la pill ("Disponible en ..."): una key por modal, para
 * que el copy se edite desde las traducciones sin tocar el codigo. `plan` sólo define
 * el tema de color.
 *
 * Todo lleva defaultValue inline: las traducciones vienen de la API y es lo unico
 * verificable sin la app corriendo.
 */

const COPY = {
  // --- Pro (compactas, sin bullets) ------------------------------------------
  "events.advanced_config": {
    icon: "settings",
    plan: "Pro",
    availability: ["plan_locked_advanced_config_available", "Disponible en Pro y Premium"],
    title: ["plan_locked_advanced_config_title", "Configuración avanzada de prácticas"],
    description: [
      "plan_locked_advanced_config_desc",
      "Configurá duraciones, notificaciones y disponibilidades distintas por consultorio, cobertura o rango horario, y mucho más.",
    ],
  },
  "events.sessions": {
    icon: "calendar_clock",
    plan: "Pro",
    availability: ["plan_locked_sessions_available", "Disponible en Pro y Premium"],
    title: ["plan_locked_sessions_title", "Sesiones"],
    description: [
      "plan_locked_sessions_desc",
      "Creá turnos que se repiten automáticamente, sin tener que configurarlos uno por uno.",
    ],
  },
  "events.telemedicine": {
    icon: "videocam",
    plan: "Pro",
    availability: ["plan_locked_telemedicine_available", "Disponible en Pro y Premium"],
    title: ["plan_locked_telemedicine_title", "Telemedicina"],
    description: [
      "plan_locked_telemedicine_desc",
      "Hacé videoconsultas con tus pacientes sin salir de DrApp.",
    ],
  },
  "hce.files": {
    icon: "attach_file",
    plan: "Pro",
    availability: ["plan_locked_hce_files_available", "Disponible en Pro y Premium"],
    title: ["plan_locked_hce_files_title", "Archivos adjuntos en HCE"],
    description: [
      "plan_locked_hce_files_desc",
      "Adjuntá estudios, imágenes y documentos a la HCE de tus pacientes.",
    ],
  },
  // Techo de agendas: es un LIMIT, no una feature booleana (se bloquea comparando
  // uso contra agendaCap.value). El tope sale de la matriz o de un override por
  // team, por eso se interpola.
  "scheduling.agenda_cap": {
    icon: "stethoscope",
    plan: "Pro",
    availability: ["plan_locked_agenda_cap_available", "Disponible en Pro y Premium"],
    title: ["plan_locked_agenda_cap_title", "Más profesionales"],
    description: [
      "plan_locked_agenda_cap_desc",
      "En básico podés tener hasta {{limit}} profesionales. Agregá todos los que tu equipo necesite.",
    ],
    descriptionWithoutLimit: [
      "plan_locked_agenda_cap_desc_no_limit",
      "Llegaste al máximo de profesionales de tu plan. Agregá todos los que tu equipo necesite.",
    ],
  },

  // --- Premium (grandes, con bullets) ----------------------------------------
  "reports.bulk_export": {
    icon: "download_2",
    plan: "Premium",
    availability: ["plan_locked_bulk_export_available", "Disponible en Premium"],
    title: ["plan_locked_bulk_export_title", "Exportación masiva de HCE"],
    description: [
      "plan_locked_bulk_export_desc",
      "Descargá las historias clínicas de todos tus pacientes en un solo archivo, sin tener que exportarlas una por una.",
    ],
    benefits: [
      ["plan_locked_bulk_export_b1", "Exportás todas las historias clínicas juntas, no de a una"],
      ["plan_locked_bulk_export_b2", "Ideal para hacer un backup completo de tu cuenta"],
      ["plan_locked_bulk_export_b3", "Mismo formato que la exportación individual, lista para guardar o compartir."],
    ],
  },
  "hce.ai_summary": {
    icon: "assignment",
    plan: "Premium",
    availability: ["plan_locked_hce_ai_summary_available", "Disponible en Premium"],
    title: ["plan_locked_hce_ai_summary_title", "Resumen de HCE con IA"],
    description: [
      "plan_locked_hce_ai_summary_desc",
      "Generá un resumen automático de la historia clínica del paciente a partir de sus evoluciones. Te ahorra tiempo de lectura antes de una consulta.",
    ],
    benefits: [
      ["plan_locked_hce_ai_summary_b1", "Resume evoluciones largas en segundos"],
      ["plan_locked_hce_ai_summary_b2", "Comenzás la consulta con el contexto del paciente"],
      ["plan_locked_hce_ai_summary_b3", "Todos los resúmenes quedan guardados en la HCE"],
    ],
  },
  "hce.odontograma": {
    icon: "dentistry",
    plan: "Premium",
    availability: ["plan_locked_odontograma_available", "Disponible en Premium"],
    title: ["plan_locked_odontograma_title", "Odontograma"],
    description: [
      "plan_locked_odontograma_desc",
      "Registrá el estado dental del paciente en un gráfico interactivo. Podés marcar piezas, condiciones y tratamientos, y el registro queda en la HCE.",
    ],
    benefits: [
      ["plan_locked_odontograma_b1", "Gráfico interactivo, sin cargar todo a mano"],
      ["plan_locked_odontograma_b2", "Historial completo del estado dental por paciente"],
      ["plan_locked_odontograma_b3", "Podés dictarle el registro y lo carga automáticamente"],
    ],
  },
  "escriba.recording": {
    icon: "mic",
    plan: "Premium",
    availability: ["plan_locked_escriba_available", "Disponible en Premium"],
    title: ["plan_locked_escriba_title", "Grabar consulta"],
    description: [
      "plan_locked_escriba_desc",
      "Grabá el audio de la consulta y transcribilo automáticamente a la evolución del paciente. Sin tipear, sin perder el hilo de la consulta.",
    ],
    benefits: [
      ["plan_locked_escriba_b1", "Transcribe mientras atendés, sin tipear"],
      ["plan_locked_escriba_b2", "Queda guardado directo en la evolución del paciente"],
      ["plan_locked_escriba_b3", "Te enfocás en la consulta, no en la pantalla"],
    ],
  },
  "hce.percentiles": {
    icon: "straighten",
    plan: "Premium",
    availability: ["plan_locked_percentiles_available", "Disponible en Premium"],
    title: ["plan_locked_percentiles_title", "Percentiles"],
    description: [
      "plan_locked_percentiles_desc",
      "Graficá el crecimiento del paciente comparado con referencias poblacionales. Especialmente útil en pediatría para seguir talla, peso y perímetro cefálico a lo largo del tiempo.",
    ],
    benefits: [
      ["plan_locked_percentiles_b1", "Gráfico automático con curvas de referencia"],
      ["plan_locked_percentiles_b2", "Seguimiento histórico del paciente en un solo lugar"],
      ["plan_locked_percentiles_b3", "Pensado para uso pediátrico"],
    ],
  },
  "arca.invoicing": {
    icon: "receipt_long",
    plan: "Premium",
    availability: ["plan_locked_arca_available", "Disponible en Premium"],
    title: ["plan_locked_arca_title", "Facturación ARCA"],
    description: [
      "plan_locked_arca_desc",
      "Generá facturas electrónicas directamente desde DrApp sin salir de la plataforma. Conectá tu cuenta de ARCA y facturá tus consultas en el momento.",
    ],
    benefits: [
      ["plan_locked_arca_b1", "Se emite sola con cada turno cobrado"],
      ["plan_locked_arca_b2", "Cumplís con la normativa vigente sin cargar datos a mano"],
      ["plan_locked_arca_b3", "Todas tus facturas, en un solo lugar"],
    ],
  },
};

export default COPY;
